// v10/src/components/HelpButton.js
// v2.0 — Asistente IA de ayuda de uso (reemplaza la guía estática).
// Chat que consulta POST /api/assistant/ask (backend llama a Claude con
// el manual completo cacheado). Fase 1: solo admins/instructores.
//
// v2.1 — Global + persistente + arrastrable (desktop):
//  - Estado (isOpen, messages, input, posiciones) vive en un singleton
//    a nivel de módulo (`store`) → sobrevive al unmount/remount cuando
//    el usuario navega entre las distintas ramas de render de App.js
//    (superadmin, admin panel, selector, etc.). Reset natural en recarga.
//  - En desktop (pointer no-touch) el botón "?" y el widget son
//    arrastrables. En mobile se mantiene el comportamiento original
//    (tap para abrir, minimizar para ver detrás). Click vs drag por
//    umbral de 5px. Clamp al viewport para no perder el elemento.

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { assistantService } from '../services/apiService';

// ── Render de Markdown ────────────────────────────────────────────────
// Usamos react-markdown (librería probada, no un parser propio) para
// renderizar las respuestas del asistente. Un intento previo con parser
// casero causó un bucle infinito → NO reemplazar por lógica manual.
//
// Seguridad: NO habilitamos rehype-raw ni HTML crudo. react-markdown por
// defecto NO renderiza HTML, así que el contenido queda escapado. Además
// filtramos los esquemas de URL para bloquear cualquier link no seguro.

const safeUrlTransform = (url) => {
  const u = String(url || '').trim();
  if (/^(https?:\/\/|mailto:|\/|#)/i.test(u)) return u;
  return '';
};

const mdComponents = {
  a: ({ node, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#2563eb', textDecoration: 'underline' }}
    />
  ),
  ul: ({ node, ...props }) => <ul {...props} style={{ margin: '4px 0', paddingLeft: '22px' }} />,
  ol: ({ node, ...props }) => <ol {...props} style={{ margin: '4px 0', paddingLeft: '22px' }} />,
  li: ({ node, ...props }) => <li {...props} style={{ margin: '2px 0' }} />,
  p:  ({ node, ...props }) => <p  {...props} style={{ margin: '4px 0' }} />,
  code: ({ node, ...props }) => (
    <code
      {...props}
      style={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '0.9em',
        padding: '1px 5px',
        borderRadius: '4px',
        background: 'rgba(148,163,184,0.18)',
      }}
    />
  ),
  pre: ({ node, ...props }) => (
    <pre
      {...props}
      style={{
        margin: '4px 0',
        padding: '10px 12px',
        borderRadius: '10px',
        background: 'rgba(148,163,184,0.14)',
        overflowX: 'auto',
        fontSize: '0.9em',
      }}
    />
  ),
};

const AssistantMarkdown = ({ children }) => (
  <ReactMarkdown components={mdComponents} urlTransform={safeUrlTransform}>
    {String(children || '')}
  </ReactMarkdown>
);

// ── Store singleton a nivel de módulo ────────────────────────────────
// Sobrevive al unmount/remount de HelpButton cuando App.js cambia de
// rama (superadmin ↔ selector ↔ admin panel). Se resetea sólo al
// recargar la página (comportamiento buscado por el brief).
const store = {
  isOpen: false,
  messages: [],
  input: '',
  buttonPos: null, // { left, top } o null → usar default (bottom-right)
  widgetPos: null, // { left, top } o null → usar default
};
const listeners = new Set();
const notify = () => listeners.forEach((fn) => { try { fn(); } catch (_) {} });

const DRAG_THRESHOLD_PX = 5;

// Clamp de una posición absoluta al viewport, dejando el elemento visible.
const clampToViewport = (pos, w, h) => {
  if (!pos) return null;
  const maxLeft = Math.max(0, window.innerWidth  - w);
  const maxTop  = Math.max(0, window.innerHeight - h);
  let left = pos.left;
  let top  = pos.top;
  if (left < 0) left = 0; else if (left > maxLeft) left = maxLeft;
  if (top  < 0) top  = 0; else if (top  > maxTop)  top  = maxTop;
  return { left, top };
};

const HelpButton = ({ darkMode }) => {
  const { t, i18n } = useTranslation();

  // Estado local sincronizado con el store singleton.
  const [isOpen,     setIsOpenLocal]     = useState(store.isOpen);
  const [messages,   setMessagesLocal]   = useState(store.messages);
  const [input,      setInputLocal]      = useState(store.input);
  const [buttonPos,  setButtonPosLocal]  = useState(store.buttonPos);
  const [widgetPos,  setWidgetPosLocal]  = useState(store.widgetPos);
  const [loading,    setLoading]         = useState(false);
  const [hovered,    setHovered]         = useState(false);

  // Suscripción al store para que múltiples mounts (o remounts) queden
  // en sync. En la práctica App.js sólo renderiza un HelpButton a la
  // vez, pero suscribimos igual por robustez.
  useEffect(() => {
    const sync = () => {
      setIsOpenLocal(store.isOpen);
      setMessagesLocal(store.messages);
      setInputLocal(store.input);
      setButtonPosLocal(store.buttonPos);
      setWidgetPosLocal(store.widgetPos);
    };
    listeners.add(sync);
    // Al montar, tomar el snapshot actual (por si algo cambió antes).
    sync();
    return () => { listeners.delete(sync); };
  }, []);

  // Setters que actualizan el store y notifican.
  const setIsOpen = (v) => {
    store.isOpen = typeof v === 'function' ? v(store.isOpen) : v;
    notify();
  };
  const setMessages = (v) => {
    store.messages = typeof v === 'function' ? v(store.messages) : v;
    notify();
  };
  const setInput = (v) => {
    store.input = typeof v === 'function' ? v(store.input) : v;
    notify();
  };
  const setButtonPos = (v) => {
    store.buttonPos = typeof v === 'function' ? v(store.buttonPos) : v;
    notify();
  };
  const setWidgetPos = (v) => {
    store.widgetPos = typeof v === 'function' ? v(store.widgetPos) : v;
    notify();
  };

  const scrollRef = useRef(null);
  const inputRef  = useRef(null);
  const buttonRef = useRef(null);
  const widgetRef = useRef(null);

  // Estado del drag (por elemento) en refs para que sobreviva a los
  // re-renders que dispara setPos durante el movimiento.
  const buttonDragState = useRef({
    active: false, moved: false, pointerId: null,
    offsetX: 0, offsetY: 0, startX: 0, startY: 0, draggable: true,
  });
  const widgetDragState = useRef({
    active: false, moved: false, pointerId: null,
    offsetX: 0, offsetY: 0, startX: 0, startY: 0, draggable: true,
  });

  const currentLang = (i18n?.language || 'es').startsWith('en') ? 'en' : 'es';

  // Sugerencias iniciales por idioma
  const suggestions = currentLang === 'en'
    ? [
        'How do I add an aircraft?',
        'How do I approve a new user?',
        'What is an AOG?',
        'How do I edit the pre-flight checklist?',
      ]
    : [
        '¿Cómo agrego una aeronave?',
        '¿Cómo apruebo un usuario nuevo?',
        '¿Qué es un AOG?',
        '¿Cómo edito el checklist de prevuelo?',
      ];

  // Autoscroll al último mensaje
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Foco en input al abrir
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 250);
    }
  }, [isOpen]);

  // Permite abrir el chat desde otros componentes (ej: botón "?" del AdminDashboard).
  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener('condor360:open-assistant', open);
    return () => window.removeEventListener('condor360:open-assistant', open);
  }, []);

  // Reclamp de posiciones si el viewport cambia de tamaño.
  useEffect(() => {
    const onResize = () => {
      if (store.buttonPos && buttonRef.current) {
        const b = buttonRef.current;
        const clamped = clampToViewport(store.buttonPos, b.offsetWidth, b.offsetHeight);
        if (clamped && (clamped.left !== store.buttonPos.left || clamped.top !== store.buttonPos.top)) {
          setButtonPos(clamped);
        }
      }
      if (store.widgetPos && widgetRef.current) {
        const w = widgetRef.current;
        const clamped = clampToViewport(store.widgetPos, w.offsetWidth, w.offsetHeight);
        if (clamped && (clamped.left !== store.widgetPos.left || clamped.top !== store.widgetPos.top)) {
          setWidgetPos(clamped);
        }
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const sendQuestion = async (questionRaw) => {
    const question = String(questionRaw || '').trim();
    if (!question || loading) return;

    const newUserMsg = { role: 'user', content: question };
    const nextMessages = [...store.messages, newUserMsg];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      // Enviamos historial reciente (últimos 6 turnos) para multi-turno
      const history = nextMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .slice(-7, -1) // todo menos el turno actual (que va como `question`)
        .map(m => ({ role: m.role, content: m.content }));

      const res = await assistantService.ask(question, currentLang, history);
      const answer = res?.data?.data?.answer || '';

      if (!answer) {
        throw new Error('empty');
      }
      setMessages(prev => [...prev, { role: 'assistant', content: answer }]);
    } catch (err) {
      console.error('[HelpButton] Error asistente:', err?.message || err);
      const errMsg = currentLang === 'en'
        ? "I couldn't answer right now. Try again in a moment, or email support@condor360.app."
        : 'No pude responder ahora. Intentá de nuevo en un momento, o escribile a soporte@condor360.app.';
      setMessages(prev => [...prev, { role: 'error', content: errMsg }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendQuestion(input);
  };

  const handleClear = () => {
    setMessages([]);
    setInput('');
  };

  // Minimizar preserva la conversación (solo oculta el widget). El estado
  // vive en el store singleton → sobrevive a la navegación entre secciones.
  const handleMinimize = () => setIsOpen(false);

  // Cerrar termina la sesión: oculta el widget y limpia mensajes + input.
  const handleClose = () => {
    setIsOpen(false);
    setMessages([]);
    setInput('');
  };

  // ── Drag genérico (botón y widget) ──────────────────────────────
  // En touch NO arrastramos (evita interferir con scroll). En mouse/pen
  // arrancamos un drag potencial; si el usuario mueve >5px se convierte
  // en drag real, si no se dispara la acción de click.
  //   - frameRef: elemento a MEDIR/mover (el "marco" del widget, o el botón).
  //   - handleType: 'button' → el elemento con handlers ES el frame (no filtrar).
  //                 'header' → los handlers viven en un header interno del widget
  //                   (filtrar pointerdown si arranca sobre un botón interno como
  //                   Limpiar/Minimizar/Cerrar).
  const makeDragHandlers = ({ stateRef, frameRef, getPos, setPos, onClickIfNoDrag, handleType }) => {
    const onPointerDown = (e) => {
      const frame = frameRef.current;
      if (!frame) return;
      // Cuando el handler vive en un header (widget), evitar drag si el
      // pointer nace sobre un botón hijo (Clear/Minimize/Close).
      if (handleType === 'header') {
        const btn = e.target && e.target.closest && e.target.closest('button');
        if (btn) return;
      }
      const rect = frame.getBoundingClientRect();
      const state = stateRef.current;
      state.active = true;
      state.moved  = false;
      state.pointerId = e.pointerId;
      state.offsetX = e.clientX - rect.left;
      state.offsetY = e.clientY - rect.top;
      state.startX  = e.clientX;
      state.startY  = e.clientY;
      // touch → sólo permitimos click (no drag). mouse/pen → drag habilitado.
      state.draggable = e.pointerType !== 'touch';
      // Si arrancamos sin posición registrada, capturamos la actual como base.
      if (state.draggable && getPos() == null) {
        setPos({ left: rect.left, top: rect.top });
      }
      // Capturamos en el elemento que tiene los handlers (currentTarget) —
      // así los eventos siguientes viajan a nuestros handlers aunque el
      // pointer salga del elemento.
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    };
    const onPointerMove = (e) => {
      const state = stateRef.current;
      if (!state.active || e.pointerId !== state.pointerId || !state.draggable) return;
      if (!state.moved) {
        const dx = e.clientX - state.startX;
        const dy = e.clientY - state.startY;
        if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
        state.moved = true;
      }
      const frame = frameRef.current;
      if (!frame) return;
      const w = frame.offsetWidth;
      const h = frame.offsetHeight;
      let left = e.clientX - state.offsetX;
      let top  = e.clientY - state.offsetY;
      const maxLeft = window.innerWidth  - w;
      const maxTop  = window.innerHeight - h;
      if (left < 0) left = 0; else if (left > maxLeft) left = maxLeft;
      if (top  < 0) top  = 0; else if (top  > maxTop)  top  = maxTop;
      setPos({ left, top });
    };
    const onPointerUp = (e) => {
      const state = stateRef.current;
      if (!state.active) return;
      try { e.currentTarget && e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
      const wasClick = !state.moved;
      state.active = false;
      state.moved  = false;
      state.pointerId = null;
      if (wasClick && onClickIfNoDrag) onClickIfNoDrag();
    };
    return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp };
  };

  const buttonDrag = makeDragHandlers({
    stateRef: buttonDragState,
    frameRef: buttonRef,
    getPos: () => store.buttonPos,
    setPos: setButtonPos,
    onClickIfNoDrag: () => setIsOpen(true),
    handleType: 'button',
  });

  const widgetDrag = makeDragHandlers({
    stateRef: widgetDragState,
    frameRef: widgetRef,
    getPos: () => store.widgetPos,
    setPos: setWidgetPos,
    onClickIfNoDrag: null, // click en el header no dispara acción
    handleType: 'header',
  });

  // ─── Colores según darkMode ────────────────────────────────────
  const bg = darkMode ? '#1a1f2e' : '#ffffff';
  const border = darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)';
  const textMain = darkMode ? 'rgba(255,255,255,0.92)' : '#0f172a';
  const textSub  = darkMode ? 'rgba(255,255,255,0.5)'  : 'rgba(30,40,80,0.5)';

  // Posicionamiento: si hay pos registrada (drag), usar left/top;
  // si no, defaults (bottom/right).
  // El botón vive por defecto en bottom-right pero DESPLAZADO a la izquierda
  // (right:88 en lugar de right:24) porque en el AdminPanel la campanita de
  // notificaciones también está en bottom-right (bottom:24 right:24 z:50) y
  // tapaba al pill (z:40). Con right:88 el pill queda 20px a la izquierda de
  // la campanita, ambos visibles. En Selector la campanita está en top-right,
  // así que no hay conflicto y el pill se ve igual. El usuario siempre puede
  // arrastrar el botón si quiere reubicarlo.
  const buttonPosStyle = buttonPos
    ? { left: buttonPos.left, top: buttonPos.top, bottom: 'auto', right: 'auto' }
    : { bottom: '24px', right: '88px' };

  const widgetPosStyle = widgetPos
    ? { left: widgetPos.left, top: widgetPos.top, bottom: 'auto', right: 'auto' }
    : { bottom: '78px', right: '16px' };

  return (
    <>
      {/* ── Floating button ── */}
      <motion.button
        ref={buttonRef}
        onPointerDown={buttonDrag.onPointerDown}
        onPointerMove={buttonDrag.onPointerMove}
        onPointerUp={buttonDrag.onPointerUp}
        onPointerCancel={buttonDrag.onPointerCancel}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', delay: 0.5 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        style={{
          position: 'fixed',
          ...buttonPosStyle,
          zIndex: 40,
          display: 'flex',
          alignItems: 'center',
          gap: hovered ? '8px' : '0px',
          overflow: 'hidden',
          height: '44px',
          borderRadius: '22px',
          padding: hovered ? '0 18px 0 14px' : '0 13px',
          background: darkMode
            ? 'linear-gradient(135deg, #1e3a8a, #4c1d95)'
            : 'linear-gradient(135deg, #2563eb, #6d28d9)',
          boxShadow: '0 4px 20px rgba(37,99,235,0.35)',
          border: 'none',
          cursor: 'grab',
          transition: 'padding 0.25s ease, gap 0.25s ease, width 0.25s ease',
          color: 'white',
          touchAction: 'none',
          userSelect: 'none',
        }}
        aria-label={t('assistant_open_button', { defaultValue: 'Abrir asistente' })}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, pointerEvents: 'none' }}>
          <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.8" />
          <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="12" cy="17" r="1" fill="white" />
        </svg>
        <AnimatePresence>
          {hovered && (
            <motion.span
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                fontSize: '13px',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                display: 'block',
                pointerEvents: 'none',
              }}
            >
              {t('assistant_open_label', { defaultValue: 'Ayuda' })}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* ── Chat widget flotante (no bloqueante, sin overlay/blur) ── */}
      {/*
        Widget en la esquina inferior derecha por defecto. NO overlay full-screen:
        la app queda 100% usable detrás. Se apila JUSTO ARRIBA del botón flotante.
        Alto acotado con margen top (>= 80px) para NO tapar la campanita de
        notificaciones que vive en top:16px right:16px (App.js). z-index 45
        queda por debajo de AlertsPanel (80) y del contenedor de la campanita
        (50), así la campanita se puede seguir tocando con el chat abierto.
        Desktop: arrastrable desde el header (ver widgetDrag). Mobile: fijo.
      */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ type: 'spring', duration: 0.28 }}
            style={{
              position: 'fixed',
              ...widgetPosStyle,
              zIndex: 45,
              width: 'min(380px, calc(100vw - 32px))',
              // Alto reserva ≥ 78px arriba (campanita en top:16 + alto 44 + gap).
              // Reserva ≥ 78px abajo por el botón flotante.
              height: 'min(560px, calc(100vh - 160px))',
            }}
          >
            <div
              ref={widgetRef}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: darkMode
                  ? '0 24px 60px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.35)'
                  : '0 24px 60px rgba(15,23,42,0.22), 0 4px 16px rgba(15,23,42,0.12)',
                background: bg,
                border: border,
              }}
            >
              {/* Header (zona de agarre para el drag). Los botones internos
                  detienen el drag vía "closest button" check. */}
              <div
                onPointerDown={widgetDrag.onPointerDown}
                onPointerMove={widgetDrag.onPointerMove}
                onPointerUp={widgetDrag.onPointerUp}
                onPointerCancel={widgetDrag.onPointerCancel}
                style={{
                  padding: '16px 20px',
                  borderBottom: border,
                  background: darkMode
                    ? 'linear-gradient(135deg, #0f172a, #1e1b4b)'
                    : 'linear-gradient(135deg, #eff6ff, #f5f3ff)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  flexShrink: 0,
                  cursor: 'grab',
                  touchAction: 'none',
                  userSelect: 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(99,60,220,0.3)',
                  }}>
                    <Sparkles style={{ width: '20px', height: '20px', color: 'white' }} />
                  </div>
                  <div>
                    <h2 style={{
                      margin: 0, fontSize: '16px', fontWeight: 800,
                      color: textMain,
                    }}>
                      {t('assistant_title', { defaultValue: 'Valentina' })}
                    </h2>
                    <p style={{
                      margin: 0, fontSize: '11px', color: textSub,
                    }}>
                      {t('assistant_subtitle', { defaultValue: 'Asistente Condor360' })}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {messages.length > 0 && (
                    <button
                      onClick={handleClear}
                      style={{
                        fontSize: '11px', fontWeight: 600,
                        padding: '6px 10px', borderRadius: '10px',
                        border: 'none', cursor: 'pointer',
                        background: darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
                        color: textSub,
                      }}
                    >
                      {t('assistant_clear', { defaultValue: 'Limpiar' })}
                    </button>
                  )}
                  <motion.button
                    onClick={handleMinimize}
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    style={{
                      width: '32px', height: '32px', borderRadius: '10px',
                      border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
                      color: textSub,
                    }}
                    aria-label={t('assistant_minimize', { defaultValue: 'Minimizar' })}
                    title={t('assistant_minimize', { defaultValue: 'Minimizar' })}
                  >
                    <Minus style={{ width: '16px', height: '16px' }} />
                  </motion.button>
                  <motion.button
                    onClick={handleClose}
                    whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    style={{
                      width: '32px', height: '32px', borderRadius: '10px',
                      border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
                      color: textSub,
                    }}
                    aria-label={t('close', { defaultValue: 'Cerrar' })}
                    title={t('close', { defaultValue: 'Cerrar' })}
                  >
                    <X style={{ width: '16px', height: '16px' }} />
                  </motion.button>
                </div>
              </div>

              {/* Messages */}
              <div
                ref={scrollRef}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '16px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {messages.length === 0 && !loading && (
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: '10px',
                  }}>
                    {/* Bienvenida como burbuja del asistente — evita el hueco */}
                    {/* vacío grande del estado inicial y da personalidad. */}
                    <div style={{
                      alignSelf: 'flex-start',
                      maxWidth: '92%',
                      padding: '10px 14px',
                      borderRadius: '4px 16px 16px 16px',
                      background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(37,99,235,0.05)',
                      color: textMain,
                      fontSize: '13.5px',
                      lineHeight: 1.55,
                    }}>
                      <AssistantMarkdown>
                        {t('assistant_welcome', {
                          defaultValue: 'Hola, soy **Valentina** 👋 Puedo ayudarte a usar Condor360. Escribí tu pregunta o probá una de estas:',
                        })}
                      </AssistantMarkdown>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: '6px',
                      width: '100%',
                    }}>
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => sendQuestion(s)}
                          style={{
                            textAlign: 'left',
                            padding: '10px 14px',
                            borderRadius: '12px',
                            border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(37,99,235,0.15)',
                            background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(37,99,235,0.04)',
                            color: textMain,
                            fontSize: '13px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(37,99,235,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(37,99,235,0.04)'}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((m, idx) => {
                  if (m.role === 'user') {
                    return (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{
                          maxWidth: '80%',
                          padding: '10px 14px',
                          borderRadius: '16px 16px 4px 16px',
                          background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                          color: 'white',
                          fontSize: '13.5px',
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}>
                          {m.content}
                        </div>
                      </div>
                    );
                  }
                  if (m.role === 'error') {
                    return (
                      <div key={idx} style={{
                        alignSelf: 'flex-start',
                        maxWidth: '90%',
                        padding: '10px 14px',
                        borderRadius: '4px 16px 16px 16px',
                        background: darkMode ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.08)',
                        border: '1px solid rgba(239,68,68,0.3)',
                        color: darkMode ? '#fca5a5' : '#b91c1c',
                        fontSize: '13px',
                        lineHeight: 1.5,
                      }}>
                        {m.content}
                      </div>
                    );
                  }
                  return (
                    <div key={idx} style={{
                      alignSelf: 'flex-start',
                      maxWidth: '90%',
                      padding: '10px 14px',
                      borderRadius: '4px 16px 16px 16px',
                      background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(37,99,235,0.05)',
                      color: textMain,
                      fontSize: '13.5px',
                      lineHeight: 1.55,
                      wordBreak: 'break-word',
                    }}>
                      <AssistantMarkdown>{m.content}</AssistantMarkdown>
                    </div>
                  );
                })}

                {loading && (
                  <div style={{
                    alignSelf: 'flex-start',
                    padding: '12px 16px',
                    borderRadius: '4px 16px 16px 16px',
                    background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(37,99,235,0.05)',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}>
                    {[0, 1, 2].map(i => (
                      <motion.span
                        key={i}
                        animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                        transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
                        style={{
                          width: '6px', height: '6px', borderRadius: '50%',
                          background: darkMode ? 'rgba(255,255,255,0.6)' : '#2563eb',
                          display: 'inline-block',
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Input */}
              <form
                onSubmit={handleSubmit}
                style={{
                  padding: '12px 16px 14px',
                  borderTop: border,
                  background: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(37,99,235,0.03)',
                  display: 'flex', gap: '8px',
                  flexShrink: 0,
                }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder={t('assistant_input_placeholder', {
                    defaultValue: 'Escribí tu pregunta...',
                  })}
                  disabled={loading}
                  maxLength={1000}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                    background: darkMode ? 'rgba(255,255,255,0.05)' : 'white',
                    color: textMain,
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />
                <motion.button
                  type="submit"
                  disabled={loading || !input.trim()}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    width: '44px', height: '44px',
                    borderRadius: '12px',
                    border: 'none',
                    background: (loading || !input.trim())
                      ? (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
                      : 'linear-gradient(135deg, #2563eb, #7c3aed)',
                    color: 'white',
                    cursor: (loading || !input.trim()) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  aria-label={t('assistant_send', { defaultValue: 'Enviar' })}
                >
                  <Send style={{ width: '16px', height: '16px' }} />
                </motion.button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HelpButton;
